import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePutRtsParcelParams, TypePutRtsParcelResponse } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 처리 신청 API
 * @note 에러 처리는 사용처(모달)에서 개별 토스트로 처리한다.
 */
const fetchPutReturnDisposalRtsParcel = async (
  params: TypePutRtsParcelParams
): Promise<{ inspectionIds: number[] }> => {
  const { data } = await axiosInstance.put<TypePutRtsParcelResponse>(
    API_ENDPOINT.RECEIVE.PUT_RETURN_DISPOSAL_RTS_PARCEL,
    params
  );

  return data.data;
};

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 처리 신청 Mutation Hook
 */
export const usePutReturnDisposalRtsParcelMutation = () => {
  return useMutation({
    mutationFn: fetchPutReturnDisposalRtsParcel,
    meta: { showGlobalErrorToast: false },
  });
};

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 처리 신청 Mock
 */
registerMockAPI<TypePutRtsParcelResponse | TypeErrorResponseData>({
  method: 'put',
  endpoint: API_ENDPOINT.RECEIVE.PUT_RETURN_DISPOSAL_RTS_PARCEL,
  label: '회송폐기관리/회수(택배) 신청',
  scenarios: [
    {
      name: '신청 성공',
      response: { data: { inspectionIds: [1, 2] } },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '신청에 실패했습니다.\n다시 시도해주세요.' } },
      status: 500,
    },
    {
      name: '송장번호 다른 이슈카드 중복 (9036)',
      response: {
        error: { code: 9036, message: '이미 다른 이슈카드에 등록된 송장번호입니다.', values: ['INVOICE-0001'] },
      },
      status: 400,
    },
    {
      // 응답 송장번호가 화면 입력과 하나도 맞지 않는 경우 — 인라인 대신 토스트로 폴백하는 경로 확인용
      name: '송장번호 다른 이슈카드 중복 (9036, 입력 미매칭)',
      response: {
        error: { code: 9036, message: '이미 다른 이슈카드에 등록된 송장번호입니다.', values: ['INVOICE-9999'] },
      },
      status: 400,
    },
  ],
});
